import { useState, useEffect, useCallback, type ReactNode } from 'react'
import { ThemeContext } from '@/lib/ThemeContext'

type Theme = 'dark' | 'light' | 'system'

function getSystemTheme(): 'dark' | 'light' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function getStoredTheme(): Theme {
  return (localStorage.getItem('icc-theme') as Theme) ?? 'dark'
}

function applyTheme(theme: Theme) {
  const resolved = theme === 'system' ? getSystemTheme() : theme
  const root = document.documentElement

  root.classList.remove('theme-light', 'theme-dark', 'theme-high-contrast')

  if (resolved === 'dark') {
    root.classList.add('theme-dark')
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme)

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme)
    localStorage.setItem('icc-theme', newTheme)
    applyTheme(newTheme)
  }, [])

  useEffect(() => {
    applyTheme(theme)

    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)')
      const handler = () => applyTheme('system')
      mq.addEventListener('change', handler)
      return () => mq.removeEventListener('change', handler)
    }
  }, [theme])

  const resolvedTheme = theme === 'system' ? getSystemTheme() : theme

  return <ThemeContext value={{ theme, setTheme, resolvedTheme }}>{children}</ThemeContext>
}
